import { ArrowUpRight, Phone, Home } from "lucide-react";
import { SITE, IMAGES } from "@/lib/site";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[92vh] w-full overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Aerial view of Florida's Emerald Coast"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/65 to-ink/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-44 pb-24">
        <div className="max-w-3xl">
          <p
            className="overline text-brass animate-fade-in stagger-1"
            data-testid="hero-overline"
          >
            Florida's Emerald Coast · Florida Panhandle Realtor · Nationwide Mortgage
          </p>

          <h1
            data-testid="hero-heading"
            className="mt-6 font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight animate-fade-up stagger-2"
          >
            One trusted advisor for{" "}
            <span className="italic text-brass">the home</span> and{" "}
            <span className="italic text-brass">the loan.</span>
          </h1>

          <p
            data-testid="hero-subhead"
            className="mt-8 max-w-xl text-white/80 text-base md:text-lg leading-relaxed animate-fade-up stagger-3"
          >
            I'm J.J. Botelho — a top-producing Realtor with {SITE.affiliations.realty}{" "}
            serving Okaloosa, Walton & Santa Rosa Counties, and an experienced Mortgage
            Advisor with {SITE.affiliations.mortgage}. My team is licensed to lend in all
            50 U.S. states, including Puerto Rico, and has the ability to work with
            clients in 20 different languages. From your first showing on Florida's
            Emerald Coast to closing on an investment property anywhere in the U.S., it's
            one steady set of hands.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4 animate-fade-up stagger-4">
            <a
              href={SITE.mortgageApplyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-apply-btn"
              className="group inline-flex items-center justify-center gap-2 bg-brass text-ink px-7 py-4 rounded-full text-sm font-semibold tracking-wide hover:bg-white transition-all duration-300"
            >
              Start Your Mortgage Application
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href={SITE.homesAgentUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-listings-btn"
              className="group inline-flex items-center justify-center gap-2 bg-white text-ink px-7 py-4 rounded-full text-sm font-semibold tracking-wide hover:bg-brass transition-all duration-300"
            >
              <Home className="w-4 h-4" />
              Active Listings
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href={SITE.homeBuyingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-buy-btn"
              className="group inline-flex items-center justify-center gap-2 bg-white/90 text-ink px-7 py-4 rounded-full text-sm font-semibold tracking-wide hover:bg-brass transition-all duration-300"
            >
              <Home className="w-4 h-4" />
              Looking to Buy a Home
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href={SITE.homeSellingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-sell-btn"
              className="group inline-flex items-center justify-center gap-2 bg-white/90 text-ink px-7 py-4 rounded-full text-sm font-semibold tracking-wide hover:bg-brass transition-all duration-300"
            >
              <Home className="w-4 h-4" />
              Looking to Sell a Home
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

              <div className="mt-16 max-w-2xl animate-fade-up stagger-5">
            <p className="overline text-brass">A promise</p>
            <p
              data-testid="hero-tagline"
              className="mt-4 font-display italic text-white text-2xl sm:text-3xl md:text-4xl leading-[1.15] tracking-tight"
            >
              The last real estate professional
              <br className="hidden sm:block" />{" "}
              you will ever need.
            </p>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 right-6 md:right-12 z-10 hidden md:flex items-center gap-2 text-white/70 text-xs tracking-widest uppercase">
        <span>Scroll</span>
        <span className="h-px w-12 bg-white/40" />
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-white text-3xl">{value}</div>
      <div className="text-white/60 text-xs tracking-widest uppercase mt-1">
        {label}
      </div>
    </div>
  );
}
